import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { MessageCircle, X, Send, Loader2, LifeBuoy, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";

const QUICK = [
  { label: "İlan Ver", to: "/ilan-ver" },
  { label: "Bakiye Yükle", to: "/cuzdanim" },
  { label: "Ustalar", to: "/ustalar" },
  { label: "İş İlanları", to: "/ilanlar" },
];

export function SupportWidget() {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("chat"); // chat | ticket
  const [messages, setMessages] = useState([{ role: "assistant", content: "Merhaba! Ben Ustayla destek asistanıyım 👋 Size nasıl yardımcı olabilirim?" }]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [ticket, setTicket] = useState({ subject: "", message: "", contact: "" });
  const endRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open]);

  const send = async () => {
    if (!text.trim()) return;
    const userMsg = { role: "user", content: text };
    const history = [...messages, userMsg];
    setMessages(history);
    setText("");
    setLoading(true);
    try {
      const { data } = await api.post("/support/chat", { message: userMsg.content, history: messages });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Şu an yanıt veremiyorum. Destek talebi oluşturabilirsiniz." }]);
    } finally { setLoading(false); }
  };

  const submitTicket = async () => {
    if (!ticket.subject || !ticket.message) { toast.error("Konu ve mesaj gerekli"); return; }
    try {
      await api.post("/support/ticket", ticket);
      toast.success("Destek talebiniz alındı!");
      setTicket({ subject: "", message: "", contact: "" });
      setView("chat");
      setMessages((m) => [...m, { role: "assistant", content: "Destek talebiniz oluşturuldu. Ekibimiz en kısa sürede dönüş yapacak." }]);
    } catch { toast.error("Talep gönderilemedi"); }
  };

  return (
    <>
      {!open && (
        <button data-testid="support-widget-toggle" onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-50 h-14 w-14 rounded-full brand-gradient text-white shadow-xl shadow-emerald-900/25 flex items-center justify-center hover:scale-105 transition-transform">
          <MessageCircle className="h-6 w-6" />
          <span className="absolute -top-0.5 -right-0.5 h-3 w-3 rounded-full bg-amber-400 border-2 border-white" />
        </button>
      )}

      {open && (
        <div className="fixed bottom-5 right-5 z-50 w-[92vw] max-w-sm bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden animate-fade-up" style={{ height: "min(560px, 80vh)" }} data-testid="support-widget-panel">
          {/* header */}
          <div className="brand-gradient text-white p-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="h-9 w-9 rounded-full bg-white/20 flex items-center justify-center"><LifeBuoy className="h-5 w-5" /></span>
              <div><div className="font-bold">Ustayla Destek</div><div className="text-[11px] text-emerald-50">7/24 Akıllı Asistan</div></div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="support-widget-close" className="hover:bg-white/20 rounded-lg p-1"><X className="h-5 w-5" /></button>
          </div>

          {/* tabs */}
          <div className="flex border-b border-slate-100">
            <button onClick={() => setView("chat")} className={`flex-1 py-2.5 text-sm font-semibold ${view === "chat" ? "text-emerald-600 border-b-2 border-emerald-500" : "text-slate-400"}`}>Sohbet</button>
            <button onClick={() => setView("ticket")} data-testid="support-ticket-tab" className={`flex-1 py-2.5 text-sm font-semibold ${view === "ticket" ? "text-emerald-600 border-b-2 border-emerald-500" : "text-slate-400"}`}>Destek Talebi</button>
          </div>

          {view === "chat" ? (
            <>
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm ${m.role === "user" ? "brand-gradient text-white" : "bg-slate-100 text-slate-700"}`}>{m.content}</div>
                  </div>
                ))}
                {loading && <div className="flex justify-start"><div className="bg-slate-100 rounded-2xl px-3.5 py-2"><Loader2 className="h-4 w-4 animate-spin text-emerald-500" /></div></div>}
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {QUICK.map((q) => (
                    <button key={q.to} onClick={() => { navigate(q.to); setOpen(false); }} data-testid={`support-quick-${q.to}`}
                      className="text-xs font-semibold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full hover:bg-emerald-100 flex items-center gap-1">
                      {q.label} <ArrowUpRight className="h-3 w-3" />
                    </button>
                  ))}
                </div>
                <div ref={endRef} />
              </div>
              <div className="p-3 border-t border-slate-100">
                <a href="https://wa.me/905000000000" target="_blank" rel="noreferrer" className="block text-center text-xs text-emerald-600 font-semibold mb-2">💬 WhatsApp Destek Hattı</a>
                <div className="flex gap-2">
                  <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Sorunuzu yazın..." className="rounded-xl" data-testid="support-chat-input" />
                  <Button onClick={send} disabled={loading} className="brand-gradient text-white rounded-xl" data-testid="support-chat-send"><Send className="h-4 w-4" /></Button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              <p className="text-sm text-slate-500">Karmaşık bir durum mu var? Talebinizi bırakın, yetkiliye iletelim.</p>
              <Input value={ticket.subject} onChange={(e) => setTicket({ ...ticket, subject: e.target.value })} placeholder="Konu" className="rounded-xl" data-testid="support-ticket-subject" />
              <Input value={ticket.contact} onChange={(e) => setTicket({ ...ticket, contact: e.target.value })} placeholder="İletişim (e-posta/telefon)" className="rounded-xl" />
              <Textarea value={ticket.message} onChange={(e) => setTicket({ ...ticket, message: e.target.value })} placeholder="Sorununuzu açıklayın..." className="rounded-xl min-h-28" data-testid="support-ticket-message" />
              <Button onClick={submitTicket} className="w-full brand-gradient text-white rounded-xl" data-testid="support-ticket-submit">Destek Talebi Oluştur</Button>
            </div>
          )}
        </div>
      )}
    </>
  );
}
